// Authentication service for React frontend
import { buildApiUrl, API_CONFIG } from '../config/api';

class AuthService {
    constructor() {
        this.isAuthenticated = this.checkAuthStatus();
        this.csrfToken = localStorage.getItem('csrf_token');
    }

    // Check if user is authenticated
    checkAuthStatus() {
        return localStorage.getItem('isAuthenticated') === 'true';
    }

    // Login user
    async login(username, password) {
        try {
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOGIN), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ username, password }),
                credentials: 'include'
            });

            const data = await response.json();
            console.log('Login response data:', data);

            if (response.ok) {
                // Store authentication data
                localStorage.setItem('isAuthenticated', 'true');
                localStorage.setItem('csrf_token', data.csrf_token);
                
                // Check if user data exists, if not create a basic user object
                const userData = data.user || { username: 'admin', role: 'admin' };
                localStorage.setItem('user', JSON.stringify(userData));
                
                console.log('Stored user data:', userData);
                
                this.isAuthenticated = true;
                this.csrfToken = data.csrf_token;
                
                return { success: true, data };
            } else {
                return { success: false, message: data.message };
            }
        } catch (error) {
            console.error('Login error:', error);
            return { success: false, message: 'Login failed. Please try again.' };
        }
    }

    // Logout user
    async logout() {
        try {
            const response = await fetch(buildApiUrl(API_CONFIG.ENDPOINTS.LOGOUT), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                credentials: 'include'
            });

            // Clear local storage regardless of response
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('csrf_token');
            localStorage.removeItem('user');
            
            this.isAuthenticated = false;
            this.csrfToken = null;

            return { success: true };
        } catch (error) {
            console.error('Logout error:', error);
            // Still clear local storage even if API call fails
            localStorage.removeItem('isAuthenticated');
            localStorage.removeItem('csrf_token');
            localStorage.removeItem('user');
            
            this.isAuthenticated = false;
            this.csrfToken = null;
            
            return { success: true };
        }
    }

    // Get current user
    getCurrentUser() {
        const userStr = localStorage.getItem('user');
        console.log('Raw user string from localStorage:', userStr);
        
        if (!userStr || userStr === 'undefined' || userStr === 'null') {
            console.log('User string is empty or invalid');
            return null;
        }
        try {
            const user = JSON.parse(userStr);
            console.log('Parsed user data:', user);
            return user;
        } catch (error) {
            console.error('Error parsing user data:', error);
            // Clear invalid user data
            localStorage.removeItem('user');
            localStorage.removeItem('isAuthenticated');
            return null;
        }
    }

    // Get CSRF token
    getCSRFToken() {
        return this.csrfToken;
    }

    // Make authenticated API request
    async authenticatedRequest(url, options = {}) {
        if (!this.isAuthenticated) {
            throw new Error('User not authenticated');
        }

        const defaultHeaders = {
            'Content-Type': 'application/json',
        };

        if (this.csrfToken) {
            defaultHeaders['X-CSRF-Token'] = this.csrfToken;
        }

        const config = {
            ...options,
            headers: {
                ...defaultHeaders,
                ...options.headers,
            },
            credentials: 'include'
        };

        console.log('Making authenticated request to:', url);
        console.log('Request config:', config);

        try {
            const response = await fetch(url, config);
            console.log('Response status:', response.status);
            console.log('Response headers:', response.headers);

            // If unauthorized, try to logout
            if (response.status === 401) {
                await this.logout();
                throw new Error('Session expired. Please login again.');
            }

            return response;
        } catch (error) {
            console.error('Fetch error:', error);
            console.error('Request URL:', url);
            console.error('Request config:', config);
            
            // Check if it's a network error
            if (error.name === 'TypeError' && error.message.includes('Failed to fetch')) {
                throw new Error(`Network error: Unable to connect to ${url}. Please check if your backend server is running.`);
            }
            
            throw error;
        }
    }

    // Create blog post with authentication
    async createBlogPost(postData) {
        const url = buildApiUrl(API_CONFIG.ENDPOINTS.CREATE_POST);
        console.log('Creating blog post at URL:', url);
        console.log('Post data:', postData);
        
        try {
            return await this.authenticatedRequest(url, {
                method: 'POST',
                body: JSON.stringify(postData)
            });
        } catch (error) {
            console.error('Error in createBlogPost:', error);
            console.error('Request URL:', url);
            console.error('Request data:', postData);
            throw error;
        }
    }

    // Update blog post with authentication
    async updateBlogPost(postData) {
        return this.authenticatedRequest(
            buildApiUrl(API_CONFIG.ENDPOINTS.UPDATE_POST),
            {
                method: 'PUT',
                body: JSON.stringify(postData)
            }
        );
    }

    // Delete blog post with authentication
    async deleteBlogPost(id) {
        return this.authenticatedRequest(
            buildApiUrl(API_CONFIG.ENDPOINTS.DELETE_POST, { id }),
            {
                method: 'DELETE'
            }
        );
    }
}

// Create singleton instance
const authService = new AuthService();

export default authService;
